import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { IonIcon, IonProgressBar, IonSkeletonText, ModalController } from "@ionic/angular/standalone";
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent } from "@qid/super";
import { DigilockerRequestType } from '../../enum/digilocker-request-type';
import { RequestStatus } from "../../enum/request-status.enum";
import { DigilockerRequest, DigilockerService } from '../../modules/digilocker-request';
import { DocType } from '../../modules/sid';
import { FaceId, FaceIdRepository } from '../../modules/face-id';
import { ImageUtil } from '@qid/core';

enum DigilockerErrorCode {
  FACE_NOT_MATCHED = 'FACE_NOT_MATCHED'
}
@Component({
  selector: 'digilocker',
  templateUrl: './digilocker.component.html',
  styleUrls: ['./digilocker.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    SexyModule,
    SuperButtonComponent,
    IonSkeletonText,
    IonIcon,
    IonProgressBar
  ],
})
export class DigilockerComponent implements AfterViewInit, OnDestroy {

  @Input() docType: DocType;

  @Output() onSuccess: EventEmitter<void> = new EventEmitter<void>();

  public DigilockerErrorCode = DigilockerErrorCode;
  public digilockerUrl: string = null;
  public digilockerRequest: DigilockerRequest = null;
  public idVerificationStatus: 'started' | 'success' | 'failed' = null;
  private digilockerWindow: Window | null = null;
  public faceId: FaceId = this.faceIdRepository.faceId;
  public ImageUtil = ImageUtil;
  /** True while fetching the DigiLocker URL (no window.open yet — avoids popup blockers). */
  public isPreparingDigilocker = false;
  public prepareError: string | null = null;
  /** If true, Retry should call {@link openDigilockerLink} (URL ready; only the tab open was blocked). */
  public openBlockedByBrowser = false;

  constructor(private digilockerService: DigilockerService,
    private modalController: ModalController,
    private faceIdRepository: FaceIdRepository
  ) {
  }

  ngAfterViewInit(): void {
    queueMicrotask(() => void this.prepareDigilockerLink());
  }

  ngOnDestroy(): void {
    const w = this.digilockerWindow;
    if (w && !w.closed && this.idVerificationStatus === null) {
      w.close();
    }
  }

  async prepareDigilockerLink() {
    if (this.isPreparingDigilocker) {
      return;
    }
    if (this.digilockerUrl && !this.prepareError) {
      return;
    }
    this.isPreparingDigilocker = true;
    this.prepareError = null;
    this.openBlockedByBrowser = false;
    try {
      await this.createDigilockerRequest();
    } catch (e) {
      this.prepareError =
        typeof e === 'string' ? e : 'Could not start DigiLocker. Please try again.';
    } finally {
      this.isPreparingDigilocker = false;
    }
  }

  async createDigilockerRequest() {
    const res = await this.digilockerService.getUrl({
      runFaceMatch: true,
      requestType: DigilockerRequestType.documentUpload,
      documentType: this.docType
    })
    if (!res || !res.data) {
      throw res.errorMessage
    }
    this.digilockerUrl = res.data.url;
    this.digilockerRequest = res.data.digilockerRequest
  }


  /**
   * Must run directly from a click/tap so the browser allows opening a new tab.
   * URL is prefetched in {@link prepareDigilockerLink} (see verified-signup flow).
   */
  openDigilockerLink(): void {
    if (!this.digilockerUrl) {
      return;
    }
    this.prepareError = null;
    this.openBlockedByBrowser = false;
    if (this.idVerificationStatus === 'started') {
      return;
    }

    const win = window.open('', '_blank');
    if (!win) {
      this.openBlockedByBrowser = true;
      this.prepareError =
        'Your browser blocked the new tab. Allow popups for this site or try again.';
      return;
    }
    this.openInWindow(win, this.digilockerUrl);
  }

  public digilockerStatusCheckInterval: any = null

  setDigilockerRequestStatusCheckInterval() {
    if (this.digilockerStatusCheckInterval) {
      clearInterval(this.digilockerStatusCheckInterval);
      this.digilockerStatusCheckInterval = null;
    }

    this.digilockerStatusCheckInterval = setInterval(async () => {

      const res = await this.digilockerService.findById(this.digilockerRequest._id)
      if (res?.data) {
        this.onDigilockerRequestFetched(res.data)
      }

    }, 2000)

  }

  private onDigilockerRequestFetched(request: DigilockerRequest) {

    this.digilockerRequest = request

    if (this.digilockerRequest.status === RequestStatus.failed) {
      clearInterval(this.digilockerStatusCheckInterval)
      this.idVerificationStatus = 'failed'
    }
    if (this.digilockerRequest.status == RequestStatus.completed) {
      clearInterval(this.digilockerStatusCheckInterval)
      this.onDigilockerRequestSuccess();
    }
  }

  private async onDigilockerRequestSuccess() {
    this.idVerificationStatus = 'success'
    if (this.digilockerStatusCheckInterval) {
      clearInterval(this.digilockerStatusCheckInterval);
      this.digilockerStatusCheckInterval = null;
    }
    this.onSuccess.emit();
    if (this.modalController) {
      this.modalController.dismiss({ status: 'success' });
    }
  }

  private openInWindow(targetWindow: Window, url: string): void {
    try {
      targetWindow.location.assign(url);
      targetWindow.focus?.();
      this.digilockerWindow = targetWindow;
      this.idVerificationStatus = 'started';
      this.setDigilockerRequestStatusCheckInterval();
      return;
    } catch (error) {
      console.error('[Digilocker] failed to navigate target window', error);
    }
    this.openBlockedByBrowser = true;
    this.prepareError =
      'Your browser blocked the new tab. Allow popups for this site or try again.';
  }

  cancelDigilocker() {
    this.isPreparingDigilocker = false;
    this.prepareError = null;
    this.openBlockedByBrowser = false;
    if (this.digilockerStatusCheckInterval) {
      clearInterval(this.digilockerStatusCheckInterval)
      this.digilockerStatusCheckInterval = null
    }
    if (this.digilockerWindow) {
      this.digilockerWindow.close()
      this.digilockerWindow = null
    }
    this.digilockerUrl = null
    this.digilockerRequest = null
    this.idVerificationStatus = null

    if (this.modalController) {
      this.modalController.dismiss();
    }

  }

}
